/**
 * Client-side authentication service.
 *
 * Handles all authentication operations:
 * - Login/Register/Logout
 * - JWT token management
 * - Auto-refresh token logic
 * - OAuth flows (redirect to backend)
 * - Authentication state management
 *
 * Usage:
 * ```typescript
 * const authService = new AuthService();
 * const user = await authService.login('user@example.com', 'password');
 * ```
 */

import type {
  User,
  AuthResponse,
  RefreshResponse,
  LoginRequest,
} from '../../shared/types/auth.js';
import { formatLjubljanaTs } from '../../shared/utils/ljubljanaTime.js';
import { clearSessionHint, hasSessionHint } from '../utils/sessionHint.js';

/**
 * Authentication state change callback function type.
 */
type AuthStateCallback = (user: User | null) => void;

/**
 * AuthService - Client-side authentication management.
 *
 * This service provides a clean API for all authentication operations.
 * It automatically handles:
 * - JWT token storage and refresh
 * - API calls to backend auth endpoints
 * - OAuth redirects
 * - Authentication state notifications
 */
/** localStorage key: Ljubljana day ('YYYY-MM-DD') of the last page-load refresh (#621). */
const REFRESH_DAY_KEY = 'gsx8_refresh_day';

/** #670: a resume that has not answered by then is abandoned (the hint stays). */
const RESUME_TIMEOUT_MS = 8_000;

/**
 * A non-2xx answer from an auth endpoint. Carries the HTTP status so a caller
 * can tell a definitive refusal (401) from a transient failure (5xx).
 */
export class AuthRequestError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'AuthRequestError';
  }
}

export class AuthService {
  private currentUser: User | null = null;
  private token: string | null = null;
  private stateCallbacks: AuthStateCallback[] = [];
  private refreshTimer: number | null = null;
  private pendingUserRequest: Promise<User | null> | null = null;
  /** #670: true once this page load resumed the session from the refresh cookie. */
  private resumed = false;
  /** #670: the resume in flight — concurrent callers share one refresh. */
  private pendingRecovery: Promise<boolean> | null = null;
  /** #670: the daily refresh in flight — a second caller must not post again. */
  private dailyRefreshInFlight = false;

  /**
   * API base URL (defaults to relative paths, works in dev and production)
   */
  private readonly apiBase = '/api/auth';

  constructor() {
    // Try to restore session on initialization
    this.restoreSession();
  }

  /**
   * Restore authentication session from localStorage.
   *
   * Called on service initialization to check if user was previously logged in.
   * Note: The JWT is also stored in httpOnly cookies by the backend,
   * so even if localStorage is cleared, the backend will still recognize the user.
   */
  private restoreSession(): void {
    // Don't restore from localStorage - always check server on startup
    // This ensures we use the httpOnly cookies from OAuth
  }

  /**
   * Save authentication session to localStorage.
   *
   * Stores user info and JWT token for session persistence.
   * The JWT is ALSO stored in an httpOnly cookie by the backend.
   */
  private saveSession(user: User, token: string): void {
    this.currentUser = user;
    this.token = token;
    localStorage.setItem('gsx8_user', JSON.stringify(user));
    localStorage.setItem('gsx8_token', token);
    this.scheduleTokenRefresh();
    this.notifyStateChange();
  }

  /**
   * Clear authentication session from localStorage.
   *
   * Called on logout or when session is invalid.
   */
  private clearSession(): void {
    this.currentUser = null;
    this.token = null;
    localStorage.removeItem('gsx8_user');
    localStorage.removeItem('gsx8_token');
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.notifyStateChange();
  }

  /**
   * Schedule automatic token refresh.
   *
   * JWT tokens expire after 7 days. This schedules a refresh
   * 1 day before expiration to ensure seamless user experience.
   */
  private scheduleTokenRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Refresh token 1 day before expiration (7 days - 1 day = 6 days)
    const refreshInterval = 6 * 24 * 60 * 60 * 1000; // 6 days in milliseconds

    this.refreshTimer = window.setTimeout(() => {
      void (async () => {
        try {
          await this.refreshToken();
        } catch (error) {
          // A failed BACKGROUND refresh is not a reason to end a working
          // session: the JWT stays valid until its own expiry, and expiry
          // itself is handled where it shows (the map's session-loss guard,
          // #621). Logging out here used to throw away a live session.
          console.error('Token refresh failed:', error);
        }
      })().catch((error: unknown) =>
        console.error('src/client/services/AuthService.ts async handler failed:', error)
      );
    }, refreshInterval);
  }

  /**
   * #670: the daily refresh for a page that knows by itself that the visitor
   * is signed in (the dashboard loads its user without this service). At most
   * one refresh per Ljubljana day, no `/me` request.
   */
  keepAlive(): void {
    this.maybeRefreshDaily();
  }

  /**
   * #670: true when this page load got its session back from the refresh
   * cookie. Requests that went out before that moment were answered as an
   * anonymous visitor's — the map re-reads its context when this is true.
   */
  wasResumed(): boolean {
    return this.resumed;
  }

  /**
   * #621 — keep sessions alive: refresh the 7-day JWT once per Ljubljana day
   * on page load, so the session slides with use instead of dying 7 days
   * after login regardless of activity. Fire-and-forget; a failure only
   * warns — the current session is left exactly as it is. Since #670 every
   * refresh also extends the session on the server (30 days from now, capped
   * from sign-in) and the server sends the refresh cookie again.
   */
  private maybeRefreshDaily(): void {
    const today = formatLjubljanaTs(new Date().toISOString(), 'date');
    try {
      if (localStorage.getItem(REFRESH_DAY_KEY) === today) return;
    } catch {
      // Private mode / blocked storage: fall through and refresh — one extra
      // POST is cheaper than a stuck session.
    }
    // #670: the day is stamped only after the answer, so a second caller in
    // the meantime (the dashboard asks on load and on its first route) would
    // post again.
    if (this.dailyRefreshInFlight) return;
    this.dailyRefreshInFlight = true;
    void this.refreshToken()
      .then(() => {
        try {
          localStorage.setItem(REFRESH_DAY_KEY, today);
        } catch {
          // ignore — the next load just refreshes again
        }
      })
      .catch((error: unknown) => {
        console.warn('[AuthService] daily session refresh failed (session kept):', error);
      })
      .finally(() => {
        this.dailyRefreshInFlight = false;
      });
  }

  /**
   * Notify all registered callbacks of authentication state change.
   */
  private notifyStateChange(): void {
    this.stateCallbacks.forEach((callback) => {
      try {
        callback(this.currentUser);
      } catch (error) {
        console.error('Auth state callback error:', error);
      }
    });
  }

  /**
   * Make an authenticated API request.
   *
   * Automatically adds JWT token to Authorization header.
   * Throws an error if response is not ok.
   */
  private async apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.apiBase}${endpoint}`;

    // Build headers object
    const headers = new Headers(options.headers);
    headers.set('Content-Type', 'application/json');

    if (this.token) {
      headers.set('Authorization', `Bearer ${this.token}`);
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Important: Include cookies for httpOnly JWT
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new AuthRequestError(error.error || 'Request failed', response.status);
    }

    return response.json();
  }

  // #675: no `register()` here — the signup lives in pages/authPageData.ts
  // (it has no password and issues no session; the mailed code signs in).

  /**
   * Login with email and password.
   *
   * Supports both bcrypt and legacy HMAC-SHA1 passwords.
   * Legacy users are automatically upgraded to bcrypt on first GSX8 login.
   *
   * @param email - User's email address
   * @param password - User's password
   * @returns User object
   * @throws Error if login fails (wrong password, user not found, etc.)
   */
  async login(email: string, password: string): Promise<User> {
    const body: LoginRequest = { email, password };

    const data = await this.apiRequest<AuthResponse>('/login', {
      method: 'POST',
      body: JSON.stringify(body),
    });

    this.saveSession(data.user, data.token);
    return data.user;
  }

  /**
   * Logout the current user.
   *
   * Revokes the refresh token on the backend and clears local session.
   * The backend will also clear the httpOnly JWT cookie.
   */
  async logout(): Promise<void> {
    try {
      await this.apiRequest('/logout', {
        method: 'POST',
        body: JSON.stringify({}), // Empty body required when Content-Type is application/json
      });
    } catch (error) {
      console.error('Logout request failed:', error);
      // Continue with local logout even if backend request fails
    } finally {
      // #670: an explicit logout ends the automatic resume in this browser,
      // also when the request never reached the server.
      clearSessionHint();
      this.clearSession();
    }
  }

  /**
   * Get the currently authenticated user.
   *
   * Returns cached user if available, otherwise fetches from backend.
   * This verifies the JWT is still valid on the backend.
   *
   * @returns User object or null if not authenticated
   */
  async getCurrentUser(): Promise<User | null> {
    // Return cached user if available
    if (this.currentUser) {
      return this.currentUser;
    }

    // Return pending request if one is already in flight (prevents duplicate requests)
    if (this.pendingUserRequest) {
      return this.pendingUserRequest;
    }

    // Create and cache the pending request
    this.pendingUserRequest = this.fetchCurrentUser();

    try {
      return await this.pendingUserRequest;
    } finally {
      this.pendingUserRequest = null;
    }
  }

  private async fetchCurrentUser(): Promise<User | null> {
    // Try to fetch from backend (in case we have a valid httpOnly cookie)
    try {
      let response = await this.fetchMe();

      // #670: the 7-day ticket ran out (the browser no longer sends it) but
      // the refresh cookie may still be alive. Only a 401 starts a resume,
      // and only in a browser that carries the session hint.
      if (response.status === 401 && (await this.recoverSession())) {
        response = await this.fetchMe();
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Save to cache (but not the token since it's in httpOnly cookie)
      this.currentUser = data;
      localStorage.setItem('gsx8_user', JSON.stringify(data));
      this.notifyStateChange();

      // #621: slide the session with use (once per Ljubljana day). Deliberately
      // NOT scheduleTokenRefresh(): that 6-day timer only ever fired in a tab
      // kept open for 6 days, which is why sessions used to die on day 7.
      this.maybeRefreshDaily();

      return data;
    } catch (error) {
      console.error('Failed to get current user:', error);
      this.clearSession();
      return null;
    }
  }

  private fetchMe(): Promise<Response> {
    return fetch(`${this.apiBase}/me`, {
      method: 'GET',
      credentials: 'include',
    });
  }

  /**
   * #670: get the session back from the refresh cookie after a 401.
   *
   * Does nothing in a browser without the session hint. Callers that arrive
   * while an attempt is in flight share it (the dashboard loads several
   * panels at once).
   *
   * @returns true when the session is back and the request is worth repeating
   */
  recoverSession(): Promise<boolean> {
    if (!hasSessionHint()) {
      return Promise.resolve(false);
    }
    this.pendingRecovery ??= this.resumeSession().finally(() => {
      this.pendingRecovery = null;
    });
    return this.pendingRecovery;
  }

  /**
   * #670: one attempt to get the session back from the refresh cookie.
   *
   * The hint is deleted only when the server answered the refresh with 401 —
   * the session is gone for good. A timeout, an abort, a network error or a
   * 5xx leaves it in place, so a later page load tries again.
   *
   * @returns true when the refresh succeeded
   */
  private async resumeSession(): Promise<boolean> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), RESUME_TIMEOUT_MS);
    try {
      await this.refreshToken({ signal: controller.signal });
    } catch (error) {
      if (error instanceof AuthRequestError && error.status === 401) {
        clearSessionHint();
      }
      console.warn('[AuthService] session resume failed:', error);
      return false;
    } finally {
      clearTimeout(timer);
    }

    this.resumed = true;
    try {
      // The resume WAS today's refresh — the daily one must not run on top.
      localStorage.setItem(
        REFRESH_DAY_KEY,
        formatLjubljanaTs(new Date().toISOString(), 'date')
      );
    } catch {
      // ignore — the daily refresh just runs once more
    }
    return true;
  }

  /**
   * Refresh the JWT access token.
   *
   * Uses the refresh token (stored as httpOnly cookie) to get a new JWT.
   * Called by the daily page-load refresh, the 6-day timer, and the map's
   * session-loss guard (#621) — the latter passes an abort signal so a
   * stalled refresh cannot hold the guard open.
   *
   * The body is an explicit `{}`: apiRequest always sends
   * `Content-Type: application/json`, and Fastify answers an EMPTY JSON body
   * with 400 FST_ERR_CTP_EMPTY_JSON_BODY (verified on dev 2026-09-11) — the
   * route's body schema is all-optional, so `{}` is the only shape that
   * reaches it from a cookie-only browser call.
   *
   * @returns New JWT token
   * @throws Error if refresh fails (invalid/expired refresh token, abort)
   */
  async refreshToken(opts?: { signal?: AbortSignal }): Promise<string> {
    const data = await this.apiRequest<RefreshResponse>('/refresh', {
      method: 'POST',
      body: JSON.stringify({}),
      signal: opts?.signal,
    });

    this.token = data.token;
    try {
      localStorage.setItem('gsx8_token', data.token);
    } catch (error) {
      // Best effort: the server has already set the new cookie, so the refresh
      // HAS succeeded. A QuotaExceeded / blocked-storage throw here must not
      // turn a recovered session into an "expired" one (#621 guard).
      console.warn('[AuthService] could not persist token to localStorage:', error);
    }
    this.scheduleTokenRefresh();

    return data.token;
  }

  /**
   * Check if user is currently authenticated.
   *
   * This is a synchronous check based on cached state.
   * For server verification, use getCurrentUser() instead.
   *
   * @returns true if user has a valid token (cached)
   */
  isAuthenticated(): boolean {
    return this.currentUser !== null && this.token !== null;
  }

  /**
   * Get the current JWT token.
   *
   * Returns the cached token. Note that the token is also stored
   * in an httpOnly cookie, so this is mainly for manual API calls.
   *
   * @returns JWT token string or null if not authenticated
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Get the current user (synchronous, from cache).
   *
   * For guaranteed fresh data, use getCurrentUser() instead.
   *
   * @returns Cached user object or null
   */
  getUser(): User | null {
    return this.currentUser;
  }

  /**
   * Register a callback for authentication state changes.
   *
   * The callback will be called whenever:
   * - User logs in
   * - User logs out
   * - Session is restored
   * - Token refresh fails
   *
   * @param callback - Function to call on state change
   * @returns Unsubscribe function
   */
  onAuthStateChanged(callback: AuthStateCallback): () => void {
    this.stateCallbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.stateCallbacks.indexOf(callback);
      if (index > -1) {
        this.stateCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Initiate Google OAuth login flow.
   *
   * Redirects to backend OAuth endpoint, which will redirect to Google.
   * After user approves, Google redirects back to our callback endpoint.
   * The backend handles the callback and redirects to the frontend with auth=success.
   */
  initiateGoogleOAuth(): void {
    window.location.href = `${this.apiBase}/oauth/google`;
  }

  /**
   * Initiate Microsoft OAuth login flow.
   *
   * Redirects to backend OAuth endpoint, which will redirect to Microsoft.
   * After user approves, Microsoft redirects back to our callback endpoint.
   * The backend handles the callback and redirects to the frontend with auth=success.
   */
  initiateMicrosoftOAuth(): void {
    window.location.href = `${this.apiBase}/oauth/microsoft`;
  }

  /**
   * Handle OAuth callback after successful authentication.
   *
   * This should be called when the page loads with ?auth=success query param.
   * It fetches the current user from the backend (which has already set the cookies).
   */
  async handleOAuthCallback(): Promise<User | null> {
    try {
      const user = await this.getCurrentUser();
      if (user) {
        // Clean up the URL by removing the auth query param
        const url = new URL(window.location.href);
        url.searchParams.delete('auth');
        window.history.replaceState({}, '', url.toString());
      }
      return user;
    } catch (error) {
      console.error('OAuth callback handling failed:', error);
      return null;
    }
  }
}

// Singleton instance - use this instead of new AuthService()
export const authService = new AuthService();
